import { SchemaField, FieldType } from "./types";

let _idCounter = 0;

export function newId(): string {
    return `sf_${++_idCounter}_${Date.now()}`;
}

export function createEmptyField(): SchemaField {
    return {
        id: newId(),
        name: "",
        type: "string",
        required: false,
        description: "",
        format: "",
        enumValues: "",
        minimum: "",
        maximum: "",
        children: [],
        arrayItemType: "string",
    };
}

export function fieldToSchema(field: SchemaField): object {
    const schema: Record<string, any> = {};

    switch (field.type) {
        case "string":
            schema.type = "string";
            if (field.format) schema.format = field.format;
            if (field.enumValues.trim()) {
                schema.enum = field.enumValues.split(",").map(v => v.trim()).filter(Boolean);
            }
            break;
        case "number":
        case "integer":
            schema.type = field.type;
            if (field.minimum !== "") {
                const min = Number(field.minimum);
                if (Number.isFinite(min)) schema.minimum = min;
            }
            if (field.maximum !== "") {
                const max = Number(field.maximum);
                if (Number.isFinite(max)) schema.maximum = max;
            }
            break;
        case "boolean":
            schema.type = "boolean";
            break;
        case "object":
            schema.type = "object";
            if (field.children.length > 0) {
                schema.properties = {};
                const req: string[] = [];
                for (const child of field.children) {
                    if (child.name) {
                        schema.properties[child.name] = fieldToSchema(child);
                        if (child.required) req.push(child.name);
                    }
                }
                if (req.length) schema.required = req;
            }
            break;
        case "array":
            schema.type = "array";
            if (field.arrayItemType === "object" && field.children.length > 0) {
                const itemProps: Record<string, object> = {};
                const itemReq: string[] = [];
                for (const child of field.children) {
                    if (child.name) {
                        itemProps[child.name] = fieldToSchema(child);
                        if (child.required) itemReq.push(child.name);
                    }
                }
                schema.items = { type: "object", properties: itemProps };
                if (itemReq.length) schema.items.required = itemReq;
            } else {
                schema.items = { type: field.arrayItemType || "string" };
            }
            break;
    }

    if (field.description) schema.description = field.description;
    return schema;
}

export function fieldsToJsonSchema(fields: SchemaField[]): object {
    const properties: Record<string, object> = {};
    const required: string[] = [];

    for (const field of fields) {
        if (!field.name) continue;
        properties[field.name] = fieldToSchema(field);
        if (field.required) required.push(field.name);
    }

    const schema: Record<string, any> = { type: "object", properties };
    if (required.length) schema.required = required;
    return schema;
}

export function schemaToField(name: string, schema: Record<string, any>, isRequired: boolean): SchemaField {
    const field = createEmptyField();
    field.name = name;
    field.required = isRequired;
    field.description = schema.description || "";

    // Handle union types like ["number", "null"] — pick first non-null type
    let type: string = schema.type || "string";
    if (Array.isArray(type)) {
        type = type.find((t: string) => t !== "null") || "string";
    }

    switch (type) {
        case "string":
            field.type = "string";
            field.format = schema.format || "";
            if (Array.isArray(schema.enum)) {
                field.enumValues = schema.enum.join(",");
            }
            break;
        case "number":
        case "integer":
            field.type = type;
            if (schema.minimum != null) field.minimum = String(schema.minimum);
            if (schema.maximum != null) field.maximum = String(schema.maximum);
            break;
        case "boolean":
            field.type = "boolean";
            break;
        case "object":
            field.type = "object";
            if (schema.properties) {
                const childRequired = Array.isArray(schema.required) ? schema.required : [];
                field.children = Object.entries(schema.properties).map(([k, v]) =>
                    schemaToField(k, v as Record<string, any>, childRequired.includes(k))
                );
            }
            break;
        case "array":
            field.type = "array";
            const itemSchema = schema.items;
            const itemType = Array.isArray(itemSchema?.type)
                ? (itemSchema.type.find((t: string) => t !== "null") || "string")
                : (itemSchema?.type || "string");
            const supportedItemTypes = ["string", "number", "integer", "boolean"];
            if (itemType === "object" && itemSchema?.properties) {
                field.arrayItemType = "object";
                const childRequired = Array.isArray(itemSchema.required) ? itemSchema.required : [];
                field.children = Object.entries(itemSchema.properties).map(([k, v]) =>
                    schemaToField(k, v as Record<string, any>, childRequired.includes(k))
                );
            } else {
                field.arrayItemType = supportedItemTypes.includes(itemType) ? itemType : "string";
            }
            break;
    }

    return field;
}

export function parseJsonSchemaToFields(schema: any): SchemaField[] | null {
    try {
        if (!schema || typeof schema !== "object" || schema.type !== "object" || !schema.properties) return null;
        const req = Array.isArray(schema.required) ? schema.required : [];
        return Object.entries(schema.properties).map(([name, def]) =>
            schemaToField(name, def as Record<string, any>, req.includes(name))
        );
    } catch {
        return null;
    }
}

/**
 * Merge a new schema-generated sample with an existing sample, preserving
 * existing values for fields that still exist. Only adds default values
 * for newly added fields. Removes fields that no longer exist in the schema.
 */
export function mergeSampleWithSchema(fields: SchemaField[], existingSample?: object): object {
    const newSample = generateSampleFromSchema(fields);
    if (!existingSample || typeof existingSample !== "object") return newSample;

    return mergeObjects(newSample, existingSample as Record<string, any>);
}

function mergeObjects(newObj: Record<string, any>, oldObj: Record<string, any>): Record<string, any> {
    const merged: Record<string, any> = {};
    for (const [key, newValue] of Object.entries(newObj)) {
        if (key in oldObj) {
            // Field exists in old sample — preserve the old value
            const oldValue = oldObj[key];
            if (newValue && typeof newValue === "object" && !Array.isArray(newValue)
                && oldValue && typeof oldValue === "object" && !Array.isArray(oldValue)) {
                // Both are objects — merge recursively
                merged[key] = mergeObjects(newValue, oldValue);
            } else {
                merged[key] = oldValue;
            }
        } else {
            // New field — use the generated default
            merged[key] = newValue;
        }
    }
    return merged;
}

export function generateSampleFromSchema(fields: SchemaField[]): object {
    const sample: Record<string, any> = {};
    for (const field of fields) {
        if (!field.name) continue;
        switch (field.type) {
            case "string":
                if (field.format === "date-time") sample[field.name] = "2024-01-01T00:00:00Z";
                else if (field.format === "date") sample[field.name] = "2024-01-01";
                else if (field.format === "time") sample[field.name] = "12:00:00";
                else if (field.format === "email") sample[field.name] = "user@example.com";
                else if (field.format === "uri") sample[field.name] = "https://example.com";
                else if (field.format === "uuid") sample[field.name] = "00000000-0000-0000-0000-000000000000";
                else if (field.format === "ipv4") sample[field.name] = "192.168.1.1";
                else if (field.enumValues.trim()) {
                    const first = field.enumValues.split(",")[0]?.trim();
                    sample[field.name] = first || "example";
                } else sample[field.name] = "example";
                break;
            case "number": {
                const min = Number(field.minimum);
                sample[field.name] = field.minimum !== "" && Number.isFinite(min) ? min : 0;
                break;
            }
            case "integer": {
                const min = Number(field.minimum);
                sample[field.name] = field.minimum !== "" && Number.isFinite(min) ? Math.ceil(min) : 0;
                break;
            }
            case "boolean":
                sample[field.name] = true;
                break;
            case "object":
                sample[field.name] = generateSampleFromSchema(field.children);
                break;
            case "array":
                if (field.arrayItemType === "object" && field.children.length > 0) {
                    sample[field.name] = [generateSampleFromSchema(field.children)];
                } else {
                    sample[field.name] = [];
                }
                break;
        }
    }
    return sample;
}

export function inferSchemaFromSample(obj: any): object {
    if (obj === null) return { type: "null" };
    if (Array.isArray(obj)) {
        const itemSchema = obj.length > 0 ? inferSchemaFromSample(obj[0]) : {};
        return { type: "array", items: itemSchema };
    }
    if (typeof obj === "object") {
        const properties: Record<string, object> = {};
        for (const [key, value] of Object.entries(obj)) {
            properties[key] = inferSchemaFromSample(value);
        }
        const schema: Record<string, any> = { type: "object", properties };
        return schema;
    }
    if (typeof obj === "number") {
        return Number.isInteger(obj) ? { type: "integer" } : { type: "number" };
    }
    if (typeof obj === "boolean") return { type: "boolean" };
    return { type: "string" };
}
